import {
  Box,
  Button,
  Grid,
  HStack,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Skeleton,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { type ReactElement, useCallback } from 'react';

import { FormatType, getFormatter } from '@revelio/replots';

import {
  type CompareCompany,
  useCompanyCompareStore,
} from '../hooks/useCompanyCompareStore';
import { useCurrencyFormatter } from '../hooks/useCurrencyFormatter';
import type { RiskLevel } from '../hooks/useEmployerHealth';
import type { RoleLevel } from '../hooks/useResolveProfileIds';
import { RISK_COLORS } from '../utils/colors';
import { useCompanyPeekData } from './useCompanyPeekData';

const pctFormatter = getFormatter(FormatType.PERCENTAGE);
const intFormatter = getFormatter(FormatType.INTEGER);

const RISK_LABELS: Record<RiskLevel, string> = {
  low: 'Low',
  medium: 'Moderate',
  high: 'High',
};

const upliftColor = (text: string): string =>
  text.startsWith('+') ? 'green.600' : 'red.600';

export interface CompanyPeekData {
  rcid: number;
  name: string;
  headcount: number | null;
  salary: number | null;
  growthRate: number | null;
  stabilityScore: number | null;
  risk: RiskLevel | null;
  sentimentOverall: number | null;
  linkedInUrl: string | null;
}

interface CompanyPeekTooltipProps {
  data: CompanyPeekData;
  userSalary: number;
  userRoleLevel: RoleLevel | null;
  children: ReactElement;
}

const StatCell = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <Box>
    <Text fontSize="2xs" color="gray.400" lineHeight="1.2">
      {label}
    </Text>
    <Text fontSize="xs" fontWeight="semibold" color={color ?? 'gray.700'}>
      {value}
    </Text>
  </Box>
);

export const CompanyPeekTooltip = ({
  data,
  userSalary,
  userRoleLevel,
  children,
}: CompanyPeekTooltipProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const salaryFormatter = useCurrencyFormatter();

  const {
    description,
    headquarters,
    founded,
    activePostingsForRole,
    headcount: lazyHeadcount,
    salary: lazySalary,
    growthRate: lazyGrowthRate,
    sentimentOverall: lazySentiment,
    isLoading: peekLoading,
  } = useCompanyPeekData(data.rcid, userRoleLevel, isOpen);

  // Merge: use parent-provided data first, fall back to lazy-fetched
  const headcount = data.headcount ?? lazyHeadcount;
  const salary = data.salary ?? lazySalary;
  const growthRate = data.growthRate ?? lazyGrowthRate;
  const sentimentOverall = data.sentimentOverall ?? lazySentiment;

  const addCompany = useCompanyCompareStore((s) => s.addCompany);
  const removeCompany = useCompanyCompareStore((s) => s.removeCompany);
  const isSelected = useCompanyCompareStore((s) => s.isSelected)(data.rcid);
  const currentEmployer = useCompanyCompareStore((s) => s.currentEmployer);
  const selectedCompanies = useCompanyCompareStore((s) => s.selectedCompanies);

  const totalCount = (currentEmployer ? 1 : 0) + selectedCompanies.length;
  const isFull = totalCount >= 4;

  const handleToggleCompare = useCallback(() => {
    const company: CompareCompany = { rcid: data.rcid, name: data.name };
    if (isSelected) {
      removeCompany(data.rcid);
    } else {
      addCompany(company);
    }
  }, [data.rcid, data.name, isSelected, addCompany, removeCompany]);

  // Compute uplift
  let upliftText: string | null = null;
  if (salary !== null && userSalary > 0) {
    const upliftPct = ((salary - userSalary) / userSalary) * 100;
    const sign = upliftPct >= 0 ? '+' : '';
    upliftText = `${sign}${upliftPct.toFixed(0)}%`;
  }

  let growthColor = 'gray.500';
  if (growthRate !== null) {
    if (growthRate > 0) growthColor = 'green.500';
    else if (growthRate < 0) growthColor = 'red.500';
  }

  return (
    <Popover
      trigger="hover"
      openDelay={300}
      closeDelay={150}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="auto"
      isLazy
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <Portal>
        <PopoverContent
          w="320px"
          boxShadow="lg"
          borderColor="gray.200"
          _focus={{ outline: 'none' }}
          zIndex="popover"
        >
          <PopoverArrow />
          <PopoverBody p={3}>
            <VStack align="stretch" spacing={2}>
              {/* Header: Name + LinkedIn */}
              <HStack justify="space-between" align="baseline">
                <Text fontSize="sm" fontWeight="bold" color="gray.800">
                  {data.name}
                </Text>
                {data.linkedInUrl && (
                  <Link
                    href={data.linkedInUrl}
                    isExternal
                    fontSize="xs"
                    color="blue.400"
                    _hover={{ color: 'blue.600' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    LinkedIn
                  </Link>
                )}
              </HStack>

              {/* Description + Location + Founded */}
              {peekLoading ? (
                <VStack align="stretch" spacing={1}>
                  <Skeleton height="12px" />
                  <Skeleton height="12px" width="60%" />
                </VStack>
              ) : (
                <>
                  {description && (
                    <Text
                      fontSize="xs"
                      color="gray.500"
                      maxH="80px"
                      overflowY="auto"
                      lineHeight="1.5"
                      sx={{
                        '&::-webkit-scrollbar': { width: '4px' },
                        '&::-webkit-scrollbar-thumb': {
                          bg: 'gray.200',
                          borderRadius: 'full',
                        },
                      }}
                    >
                      {description}
                    </Text>
                  )}
                  <HStack spacing={3} flexWrap="wrap">
                    {headquarters && (
                      <Text fontSize="xs" color="gray.500">
                        {headquarters}
                      </Text>
                    )}
                    {founded && (
                      <Text fontSize="xs" color="gray.500">
                        Est. {founded}
                      </Text>
                    )}
                  </HStack>
                </>
              )}

              {/* Key Stats Grid */}
              <Box borderTop="1px solid" borderColor="gray.100" pt={2}>
                <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                  <StatCell
                    label="Headcount"
                    value={
                      headcount !== null ? intFormatter(headcount) : '\u2014'
                    }
                  />
                  <StatCell
                    label="Median Salary"
                    value={
                      salary !== null
                        ? `${salaryFormatter(salary)}${upliftText ? ` (${upliftText})` : ''}`
                        : '\u2014'
                    }
                    color={upliftText ? upliftColor(upliftText) : undefined}
                  />
                  <StatCell
                    label="Growth"
                    value={
                      growthRate !== null ? pctFormatter(growthRate) : '\u2014'
                    }
                    color={growthColor}
                  />
                  <StatCell
                    label="Stability"
                    value={
                      data.stabilityScore !== null
                        ? `${data.stabilityScore}/100`
                        : '\u2014'
                    }
                    color={data.risk ? RISK_COLORS[data.risk] : undefined}
                  />
                  <StatCell
                    label="Sentiment"
                    value={
                      sentimentOverall !== null
                        ? `${(sentimentOverall * 5).toFixed(1)}/5`
                        : '\u2014'
                    }
                  />
                  <StatCell
                    label="Risk"
                    value={data.risk ? RISK_LABELS[data.risk] : '\u2014'}
                    color={data.risk ? RISK_COLORS[data.risk] : undefined}
                  />
                </Grid>
              </Box>

              {/* Postings for role */}
              <Box borderTop="1px solid" borderColor="gray.100" pt={2}>
                {(() => {
                  if (peekLoading) {
                    return <Skeleton height="14px" width="70%" />;
                  }
                  if (
                    activePostingsForRole !== null &&
                    activePostingsForRole > 0
                  ) {
                    return (
                      <Text fontSize="xs" color="blue.600" fontWeight="medium">
                        {activePostingsForRole} active posting
                        {activePostingsForRole !== 1 ? 's' : ''} for your role
                      </Text>
                    );
                  }
                  return (
                    <Text fontSize="xs" color="gray.400">
                      No current postings for your role
                    </Text>
                  );
                })()}
              </Box>

              {/* Add to Compare Button */}
              <Button
                size="xs"
                variant={isSelected ? 'solid' : 'outline'}
                colorScheme={isSelected ? 'green' : 'blue'}
                onClick={handleToggleCompare}
                isDisabled={!isSelected && isFull}
                w="100%"
              >
                {isSelected ? 'Added to Compare' : 'Add to Compare'}
              </Button>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
